import { Grid, Stack, Typography } from "@mui/material";
import type { FC } from "react";
import { AssociatedFilesView } from "../../components/associatedFiles/AssociatedFilesView";
import CustomerLink from "../../components/customerLink/CustomerLink";
import EntityCard from "../../components/entityCard/EntityCard";
import type { Currency } from "../../data/BaseEntity";
import {
  formatDate,
  getFreeCancellationDays,
  getOrderDisplayMode,
  getOrderListDisplayMode,
  isAdvanceAwaitingPayment,
} from "../../data/dataUtils";
import dayjs, { ISO_DATE_FORMAT } from "../../dayjs";
import type { UserRole } from "../../auth/auth";
import { getOrderPrice } from "../csvExport/getOrderPrice";
import OrderInvoices from "../invoices/OrderInvoices";
import { moneyText } from "../orderEdit/MoneyValue";
import OrderStatusView from "../orderStatus/OrderStatusView";
import type { SavedOrderWithNames } from "../orderTypes";
import OrderCardProducts from "./OrderCardProducts";
import { DisplayModeAccent } from "./orderCardUtils";
import OrderCardNotes from "./OrderCardNotes";
import ReadonlyOrderTodos from "../todos/ReadonlyOrderTodos";
import { shouldShowTodos } from "../todos/shouldShowTodos";

const Balance: FC<{ order: SavedOrderWithNames; paid: number }> = ({ order, paid }) => {
  const balance = getOrderPrice(order).subtract(paid);
  const amount = balance.value;
  const text =
    typeof amount === "number"
      ? moneyText(amount, order.currency, "Saldo")
      : `Saldo: ${balance.toDisplayValue(order.currency)}`;
  return <Typography>{text}</Typography>;
};

const Paid: FC<{ paid: number; currency: Currency }> = ({ paid, currency }) => (
  <Typography>{moneyText(paid, currency, "Wpłacono")}</Typography>
);

const FreeCancellationDays: FC<{ freeCancellationDate: string | undefined }> = ({ freeCancellationDate }) => {
  if (!freeCancellationDate) {
    return null;
  }
  const freeCancellationDays = getFreeCancellationDays(freeCancellationDate);
  return (
    <Typography color="darkgreen">
      Anulacja do: {dayjs(freeCancellationDate, ISO_DATE_FORMAT).format("DD-MM-YYYY")}
      {freeCancellationDays !== undefined && freeCancellationDays >= 0 && (
        <span className="card-header-zamowienie-anulacja" style={{ paddingLeft: 5 }}>
          (dni: {freeCancellationDays})
        </span>
      )}
    </Typography>
  );
};

interface AdvancePaymentDeadlineProps {
  readonly advanceDeadline: string | undefined;
  readonly highlighted?: boolean;
}

const AdvancePaymentDeadline: FC<AdvancePaymentDeadlineProps> = ({ advanceDeadline, highlighted = false }) => {
  if (!advanceDeadline) {
    return null;
  }
  return (
    <Typography color={highlighted ? "error" : "darkgreen"} sx={{ fontWeight: highlighted ? "bold" : undefined }}>
      Termin zaliczki: {formatDate(advanceDeadline)}
    </Typography>
  );
};

const ContactDate: FC<{ contactDate: string | undefined }> = ({ contactDate }) => {
  if (!contactDate) {
    return null;
  }
  return <Typography color="darkgreen">Kontakt do: {dayjs(contactDate).format("DD-MM-YYYY")}</Typography>;
};

const OrderName: FC<{ name: string; id?: number }> = ({ name, id }) => {
  const value = id ? `${id}: ${name}` : name;
  return <Typography variant="subtitle1">{value}</Typography>;
};

interface Props {
  readonly value: SavedOrderWithNames;
  readonly showHistorical: boolean;
  readonly role: UserRole | undefined;
  readonly onClick: () => void;
  readonly onContextMenu: () => void;
}

export const OrderCard: FC<Props> = ({ value, showHistorical, role, onClick, onContextMenu }) => {
  const isAdminOrAgent = role === "ADMIN" || role === "AGENT";
  const displayMode = isAdminOrAgent ? getOrderListDisplayMode(value) : getOrderDisplayMode(value);

  return (
    <EntityCard
      role={role}
      accent={DisplayModeAccent[displayMode]}
      onClick={onClick}
      onContextMenu={onContextMenu}
      rightHandSideComponent={shouldShowTodos(value) ? <ReadonlyOrderTodos todos={value.todos} /> : undefined}
      expandableComponent={<OrderCardNotes orderId={value.id} contactDate={value.contactDate} />}
    >
      <Grid size={{ xs: 12, lg: 4 }}>
        <Stack spacing={1}>
          {isAdminOrAgent && (
            <Stack
              direction="row"
              sx={{
                justifyContent: "space-between",
              }}
            >
              <Stack direction="row" spacing={1}>
                <Typography variant="body2">
                  <CustomerLink customerId={value.customerId} underline="hover" color="inherit">
                    {value.customerName}
                  </CustomerLink>
                </Typography>
                {value.customerAccountManagerName && (
                  <Typography variant="body2">({value.customerAccountManagerName})</Typography>
                )}
              </Stack>
              {value.supervisorName && <Typography variant="body2">{value.supervisorName}</Typography>}
            </Stack>
          )}
          {!isAdminOrAgent && <Typography variant="body2">{value.customerContactPersonName}</Typography>}
          <OrderName name={value.name} id={value.userFriendlyId} />
          {isAdminOrAgent ? (
            <FreeCancellationDays freeCancellationDate={value.freeCancellationDate} />
          ) : (
            <AdvancePaymentDeadline advanceDeadline={value.advance.deadline} />
          )}
          {isAdminOrAgent ? <ContactDate contactDate={value.contactDate} /> : null}
          <Stack
            direction="row"
            sx={{
              justifyContent: "space-between",
            }}
          >
            <OrderStatusView value={value.status} />
            <Stack direction="column">
              <Paid paid={value.paymentSum} currency={value.currency} />
              <Balance order={value} paid={value.paymentSum} />
              {isAdminOrAgent && isAdvanceAwaitingPayment(value) && (
                <AdvancePaymentDeadline advanceDeadline={value.advance.deadline} highlighted />
              )}
            </Stack>
          </Stack>
        </Stack>
      </Grid>
      <Grid size={{ xs: 12, lg: 8 }} container spacing={2}>
        <Grid size={12} onClick={(e) => e.stopPropagation()}>
          <OrderCardProducts products={value.products} showHistorical={showHistorical} />
        </Grid>
        <Grid size={12}>
          <Stack direction="column" spacing={1}>
            <OrderInvoices order={value} readonly />
            {!isAdminOrAgent && <AssociatedFilesView values={value.files?.filter((f) => f.visibility === "PUBLIC")} />}
          </Stack>
        </Grid>
      </Grid>
    </EntityCard>
  );
};
